import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { KaiProvider } from "@/contexts/KaiContext";
import { SkillThemeProvider } from "@/contexts/SkillThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { FeedbackButton } from "@/components/FeedbackButton";
import { RepublicDayBanner } from "@/components/RepublicDayBanner";
import { GlobalKai } from "@/components/GlobalKai";
// BetaDisclaimer removed - no longer needed for production
import ErrorBoundary from "@/components/ErrorBoundary";
import { GlobalMessageNotifications } from "@/components/GlobalMessageNotifications";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { OfflineBanner } from "@/components/OfflineBanner";
import { processPendingCompletions } from "@/lib/completionGuarantee";

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Quiz = lazy(() => import("./pages/Quiz"));
const FindMySkill = lazy(() => import("./pages/FindMySkill"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Chat = lazy(() => import("./pages/Chat"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const SkillCategory = lazy(() => import("./pages/SkillCategory"));
const LearnSkill = lazy(() => import("./pages/LearnSkill"));
const KaiStore = lazy(() => import("./pages/KaiStore"));
const SkillHome = lazy(() => import("./pages/SkillHome"));
const SpecialElements = lazy(() => import("./pages/SpecialElements"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Community = lazy(() => import("./pages/Community"));
const RoadmapPage = lazy(() => import("./pages/RoadmapPage"));
const LessonPage = lazy(() => import("./pages/LessonPage"));
const Missions = lazy(() => import("./pages/Missions"));
const SubmissionsHistory = lazy(() => import("./pages/SubmissionsHistory"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Settings = lazy(() => import("./pages/Settings"));
const WebDevLearning = lazy(() => import("./pages/WebDevLearning"));
const AIAutomationLearning = lazy(() => import("./pages/AIAutomationLearning"));
const EntrepreneurshipLearning = lazy(() => import("./pages/EntrepreneurshipLearning"));
const VideoEditingLearning = lazy(() => import("./pages/VideoEditingLearning"));
const EditingLearning = lazy(() => import("./pages/EditingLearning"));
const PublicSpeakingLearning = lazy(() => import("./pages/PublicSpeakingLearning"));
const ProgressDashboard = lazy(() => import("./pages/ProgressDashboard"));
const Chats = lazy(() => import("./pages/Chats"));
const AIWebsiteBuilder = lazy(() => import("./pages/AIWebsiteBuilder"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const Founder = lazy(() => import("./pages/Founder"));
const Press = lazy(() => import("./pages/Press"));
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Careers = lazy(() => import("./pages/Careers"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Register service worker with auto-update handling
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        
        // Check for updates periodically (every 5 minutes)
        setInterval(() => {
          registration.update();
        }, 5 * 60 * 1000);

        // Handle service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content available - auto reload for seamless update
                console.log('[PWA] New version available, reloading...');
                window.location.reload();
              }
            });
          }
        });
      } catch (error) {
        console.log('[PWA] SW registration failed:', error);
      }
    });

    // Listen for SW update messages
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        console.log('[PWA] Service worker updated to version:', event.data.version);
        // Reload to get fresh content
        window.location.reload();
      }
    });

    // Handle controller change (new SW took over)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[PWA] New service worker activated, refreshing...');
      window.location.reload();
    });
  }
};

// Component to process pending completions on auth
const PendingCompletionProcessor = () => {
  const { user } = useAuth();
  
  useEffect(() => {
    if (user?.id) {
      // Process any pending completions that failed to sync
      processPendingCompletions(user.id);
    }
  }, [user?.id]);
  
  return null;
};

const App = () => {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <KaiProvider>
          <SkillThemeProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/category/:category" element={<SkillCategory />} />
                    <Route path="/learn-skill/:skillId" element={<LearnSkill />} />
                    <Route path="/roadmap/:skillId" element={<RoadmapPage />} />
                    <Route path="/lesson/:lessonId" element={<LessonPage />} />
                    <Route path="/find-my-skill" element={<FindMySkill />} />
                    <Route path="/kai-store" element={<KaiStore />} />
                    <Route path="/skill-home" element={<SkillHome />} />
                    <Route path="/special-elements" element={<ProtectedRoute><SpecialElements /></ProtectedRoute>} />
                    <Route path="/profile/:userId" element={<UserProfile />} />
                    <Route path="/user/:userId" element={<UserProfile />} />
                    <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
                    <Route path="/community" element={<Community />} />
                    <Route path="/chats" element={<ProtectedRoute><Chats /></ProtectedRoute>} />
                    <Route path="/messages" element={<ChatPage />} />
                    <Route path="/missions" element={<Missions />} />
                    <Route path="/submissions" element={<ProtectedRoute><SubmissionsHistory /></ProtectedRoute>} />
                    <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="/web-dev-learning" element={<ProtectedRoute><WebDevLearning /></ProtectedRoute>} />
                    <Route path="/ai-automation-learning" element={<ProtectedRoute><AIAutomationLearning /></ProtectedRoute>} />
                    <Route path="/entrepreneurship-learning" element={<ProtectedRoute><EntrepreneurshipLearning /></ProtectedRoute>} />
                    <Route path="/video-editing-learning" element={<ProtectedRoute><VideoEditingLearning /></ProtectedRoute>} />
                    <Route path="/editing-learning" element={<ProtectedRoute><EditingLearning /></ProtectedRoute>} />
                    <Route path="/public-speaking-learning" element={<ProtectedRoute><PublicSpeakingLearning /></ProtectedRoute>} />
                    <Route path="/progress-dashboard" element={<ProtectedRoute><ProgressDashboard /></ProtectedRoute>} />
                    <Route path="/ai-builder" element={<ProtectedRoute><AIWebsiteBuilder /></ProtectedRoute>} />
                    <Route path="/about" element={<About />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/founder" element={<Founder />} />
                    <Route path="/press" element={<Press />} />
                    <Route path="/features" element={<Features />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/careers" element={<Careers />} />
                    <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <GlobalKai />
                  <GlobalMessageNotifications />
                  <PWAInstallPrompt />
                  <PendingCompletionProcessor />
                </Suspense>
                <OfflineBanner />
                <RepublicDayBanner />
                <FeedbackButton />
              </BrowserRouter>
            </TooltipProvider>
          </SkillThemeProvider>
        </KaiProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
